import React, { PureComponent } from 'react';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { createOptionalContext } from '../../../../utils/create-optional-context.js';
import { ActionStyle } from '../../../action/action-constants.js';
import { HeadingType } from '../heading/heading-constants.js';
const [HeadingContext, HeadingContextConsumer, HeadingContextProvider, useHeading,] = createOptionalContext('HeadingContext');
class HeadingProvider extends PureComponent {
    constructor() {
        super(...arguments);
        this.actionListeners = new Set();
        this.customClassNameListeners = new Set();
        this.dynamicContentListeners = new Set();
        this.optionListeners = new Set();
        this.titleListeners = new Set();
        this.actions = new Map();
        this.customClassNames = [];
        this.dynamicContent = new Map();
        this.options = new Map();
        this.title = '';
        this.identifier = generateUUID();
        this.actionStylesPerContextType = {
            [HeadingType.CONTEXTUAL_BAR]: [
                ActionStyle.BUTTON,
                ActionStyle.CHECKBOX,
                ActionStyle.DROPDOWN,
                ActionStyle.ICON,
                ActionStyle.INLINE_IMAGE,
                ActionStyle.OPTION,
                ActionStyle.OPTION_GROUP,
                ActionStyle.TEXT,
            ],
            [HeadingType.SECTION]: [
                ActionStyle.BUTTON,
                ActionStyle.CHECKBOX,
                ActionStyle.ICON,
                ActionStyle.INLINE_IMAGE,
                ActionStyle.TEXT,
            ],
            [HeadingType.SUBSECTION]: [
                ActionStyle.BUTTON,
                ActionStyle.ICON,
                ActionStyle.INLINE_IMAGE,
                ActionStyle.TEXT,
            ],
        };
        // Listeners
        this.addListener = (propertyName, listener, scope) => {
            if (this[propertyName].has(listener)) {
                throw Error(`[Heading Context]: This listener is already listening to ${scope} changes.`);
            }
            this[propertyName].add(listener);
            return () => {
                this[propertyName].delete(listener);
            };
        };
        this.notifyListeners = (propertyName) => {
            this[propertyName].forEach((fn) => fn());
        };
        /**
         * Add a new listener that will be called everytime an action is added, updated or removed from the heading.
         *
         * @param listener - The listener to be added.
         * @returns A function to remove the added listener.
         */
        this.addActionListener = (listener) => {
            return this.addListener('actionListeners', listener, 'actions');
        };
        /**
         * Add a new listener that will be called everytime a custom className is added or removed to the heading.
         *
         * @param listener - The listener to be added.
         * @returns A function to remove the added listener.
         */
        this.addCustomClassNameListener = (listener) => {
            return this.addListener('customClassNameListeners', listener, 'customClassName');
        };
        /**
         * Add a new listener that will be called everytime that some dynamic content is added, updated or removed from the heading.
         *
         * @param listener - The listener to be added.
         * @returns A function to remove the added listener.
         */
        this.addDynamicContentListener = (listener) => {
            return this.addListener('dynamicContentListeners', listener, 'dynamic content');
        };
        /**
         * Add a new listener that will be called everytime an option is added, updated or removed from the heading.
         *
         * @param listener - The listener to be added.
         * @returns A function to remove the added listener.
         */
        this.addOptionListener = (listener) => {
            return this.addListener('optionListeners', listener, 'option');
        };
        /**
         * Add a new listener that will be called everytime the title of the heading is updated.
         *
         * @param listener - The listener to be added.
         * @returns A function to remove the added listener.
         */
        this.addTitleListener = (listener) => {
            return this.addListener('titleListeners', listener, 'title');
        };
        // Setters
        /**
         * Adds or updates a given action within the heading.
         *
         * @param action - The action to be added/updated;
         * @returns A function to remove the added/updated action.
         */
        this.addOrUpdateAction = (action) => {
            const { id, style } = action;
            const allowedActions = this.actionStylesPerContextType[this.props.type];
            const isAllowedAction = allowedActions.includes(style);
            if (!isAllowedAction) {
                throw new Error(`[Heading Context]: The ${style} action style is not allowed on ${this.props.type}. The allowed styles are: ${allowedActions.join(', ')}`);
            }
            this.actions.set(id, action);
            this.notifyListeners('actionListeners');
            return () => {
                this.actions.delete(id);
                this.notifyListeners('actionListeners');
            };
        };
        /**
         * Adds or updates a dynamic content element within the heading.
         *
         * @param dynamicContent - The content to be added/updated;
         * @returns A function to remove the added/updated content.
         */
        this.addOrUpdateDynamicContent = (dynamicContent) => {
            const { id } = dynamicContent;
            this.dynamicContent.set(id, dynamicContent);
            this.notifyListeners('dynamicContentListeners');
            return () => {
                this.dynamicContent.delete(id);
                this.notifyListeners('dynamicContentListeners');
            };
        };
        /**
         * Adds or updates a given option within the heading.
         *
         * @param option - The option to be added/updated;
         * @returns A function to remove the added/updated option.
         */
        this.addOrUpdateOption = (option) => {
            const { id } = option;
            if (this.props.type !== HeadingType.CONTEXTUAL_BAR) {
                throw new Error(`[Heading Context]: Action options are not allowed on ${this.props.type}.`);
            }
            this.options.set(id, option);
            this.notifyListeners('optionListeners');
            return () => {
                this.options.delete(id);
                this.notifyListeners('optionListeners');
            };
        };
        /**
         * Adds a new custom className to the heading.
         *
         * @param className - The custom className to be added.
         * @returns A function to remove the added className.
         */
        this.pushCustomClassName = (className) => {
            this.customClassNames.push(className);
            this.notifyListeners('customClassNameListeners');
            return () => {
                const position = this.customClassNames.indexOf(className);
                if (position < 0) {
                    return;
                }
                this.customClassNames.splice(position, 1);
                this.notifyListeners('customClassNameListeners');
            };
        };
        /**
         * Updates the heading Title
         *
         * @param title - The new heading title.
         */
        this.setTitle = (title) => {
            this.title = title;
            this.notifyListeners('titleListeners');
        };
        // Getters
        /**
         * Obtains all heading actions.
         *
         * @returns A list of all actions added to the heading.
         */
        this.getActions = () => {
            return Array.from(this.actions.values());
        };
        /**
         * Obtains the current custom className added to the ContextualBar.
         *
         * @returns The current custom className applied to the heading.
         */
        this.getCustomClassName = () => {
            const className = this.customClassNames[this.customClassNames.length - 1];
            return className ?? '';
        };
        /**
         * Obtains a list of all the dynamic content added to the heading.
         *
         * @returns A list of all dynamic content added to the heading.
         */
        this.getDynamicContent = () => {
            return Array.from(this.dynamicContent.values());
        };
        /**
         * Obtains the unique identifier of the heading.
         */
        this.getIdentifier = () => {
            return this.identifier;
        };
        /**
         * Obtains all heading options.
         *
         * @returns A list of all options added to the heading.
         */
        this.getOptions = () => {
            return Array.from(this.options.values());
        };
        /**
         * Obtains the current heading title.
         *
         * @returns The current heading title.
         */
        this.getTitle = () => {
            return this.title;
        };
        /**
         * Obtains the current heading type.
         *
         * @returns The current heading type.
         */
        this.getType = () => {
            return this.props.type;
        };
    }
    render() {
        const context = {
            addActionListener: this.addActionListener,
            addCustomClassNameListener: this.addCustomClassNameListener,
            addDynamicContentListener: this.addDynamicContentListener,
            addOptionListener: this.addOptionListener,
            addOrUpdateAction: this.addOrUpdateAction,
            addOrUpdateDynamicContent: this.addOrUpdateDynamicContent,
            addOrUpdateOption: this.addOrUpdateOption,
            addTitleListener: this.addTitleListener,
            getActions: this.getActions,
            getCustomClassName: this.getCustomClassName,
            getDynamicContent: this.getDynamicContent,
            getIdentifier: this.getIdentifier,
            getOptions: this.getOptions,
            getTitle: this.getTitle,
            getType: this.getType,
            pushCustomClassName: this.pushCustomClassName,
            setTitle: this.setTitle,
        };
        return (React.createElement(HeadingContextProvider, { value: context }, this.props.children));
    }
}
export { HeadingContext, HeadingContextConsumer, HeadingProvider, useHeading, };
//# sourceMappingURL=heading-context.js.map